import { Player } from '@/types/tournament';
import { AmericanoPlayer } from '@/contexts/tournament/types';

/**
 * Calculate total points from round scores
 */
export function calculatePlayerPoints(roundScores: number[]): number {
  return roundScores.reduce((sum, score) => sum + (score || 0), 0);
}

/**
 * Update player total points based on their round scores
 * Works for both Mexicano and Americano player types
 */
export function updatePlayerTotalPoints<T extends { points: number; roundScores?: number[] }>(players: T[]): T[] {
  return players.map(player => ({
    ...player,
    points: player.roundScores ? calculatePlayerPoints(player.roundScores) : player.points
  }));
}

/**
 * Update player scores for a specific round
 * Used by both Mexicano and Americano tournaments
 */
export function updatePlayerRoundScore<T extends { id: string; roundScores?: number[] }>(
  player: T,
  roundIndex: number,
  score: number
): T {
  const updatedPlayer = { ...player };
  
  if (!updatedPlayer.roundScores) {
    updatedPlayer.roundScores = [];
  }
  
  updatedPlayer.roundScores[roundIndex] = score;
  
  return updatedPlayer;
}

/**
 * Calculate games played from round scores
 */
export function calculateGamesPlayed(roundScores: number[]): number {
  return roundScores.filter(score => score !== undefined && score !== null).length;
}

/**
 * Sort players by total points (descending), then by name (ascending)
 * Works for both tournament types
 */
export function sortPlayersByScore<T extends { points: number; name: string }>(players: T[]): T[] {
  return [...players].sort((a, b) => {
    // Primary sort: total points (descending)
    if (b.points !== a.points) {
      return b.points - a.points;
    }
    
    // Tiebreaker: name (ascending)
    return a.name.localeCompare(b.name);
  });
}